import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../contexts/AdminContext';
import { getFormulaById, ALL_SLOTS, MAX_PLAYERS_PER_SLOT, getLoyaltyLevel } from '../config/formulas';
import PaymentBalanceModal from './PaymentBalanceModal';
import '../styles/Dashboard.css';

function Dashboard({ sessions, bookings, contacts, giftCards, onUpdateBookingPayment, onDeleteContact, onMarkGiftCardUsed, onDeleteGiftCard }) {
  const { isAdmin, logout } = useAdmin();
  const navigate = useNavigate();
  const [periodFilter, setPeriodFilter] = useState('today');
  const [contactSearch, setContactSearch] = useState('');
  const [giftCardSearch, setGiftCardSearch] = useState('');
  const [showGiftCards, setShowGiftCards] = useState(true);

  // Helper pour formater en YYYY-MM-DD sans décalage timezone
  const toDateStr = (d) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Date du jour
  const today = new Date();
  const todayStr = toDateStr(today);

  // Calculer les dates de la semaine et du mois
  const getWeekDates = () => {
    const start = new Date(today);
    const day = start.getDay();
    start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(toDateStr(d));
    }
    return dates;
  };

  const getMonthDates = () => {
    const year = today.getFullYear();
    const month = today.getMonth();
    const dates = [];
    const d = new Date(year, month, 1);
    while (d.getMonth() === month) {
      dates.push(toDateStr(d));
      d.setDate(d.getDate() + 1);
    }
    return dates;
  };

  // Filtrer les réservations par période (exclure les annulées)
  const filteredBookings = useMemo(() => {
    const active = bookings.filter(b => b.status !== 'cancelled');
    if (periodFilter === 'today') {
      return active.filter(b => b.date === todayStr);
    } else if (periodFilter === 'week') {
      const weekDates = getWeekDates();
      return active.filter(b => weekDates.includes(b.date));
    } else {
      const monthDates = getMonthDates();
      return active.filter(b => monthDates.includes(b.date));
    }
  }, [bookings, periodFilter, todayStr]);

  // =====================
  // 1. RÉSERVATIONS DU JOUR
  // =====================
  const todayBookings = useMemo(() => {
    return bookings
      .filter(b => b.date === todayStr && b.status !== 'cancelled')
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  }, [bookings, todayStr]);

  // Prochaine réservation
  const now = `${String(today.getHours()).padStart(2, '0')}:${String(today.getMinutes()).padStart(2, '0')}`;
  const nextBooking = todayBookings.find(b => (b.startTime || '') >= now);

  // =====================
  // 2. CHIFFRE D'AFFAIRES
  // =====================
  const revenue = useMemo(() => {
    const totalCA = filteredBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0);
    // Encaissé = (intégral payé) + (acomptes versés sur résa pas encore soldées)
    const paidCA = filteredBookings.reduce((sum, b) => {
      if (b.isPaid) return sum + (b.totalPrice || 0);
      if (b.depositPaid) return sum + (b.depositAmount || 0);
      return sum;
    }, 0);
    const unpaidCA = Math.round((totalCA - paidCA) * 100) / 100;
    // Acomptes en attente de solde (info dédiée)
    const pendingBalance = filteredBookings
      .filter(b => !b.isPaid && b.depositPaid)
      .reduce((sum, b) => sum + (b.remainingAmount || 0), 0);
    const nbBookings = filteredBookings.length;
    const nbPlayers = filteredBookings.reduce((sum, b) => sum + (b.nbParticipants || 0), 0);
    return { totalCA, paidCA, unpaidCA, pendingBalance, nbBookings, nbPlayers };
  }, [filteredBookings]);

  // CA par jour pour la semaine
  const weeklyBreakdown = useMemo(() => {
    const weekDates = getWeekDates();
    return weekDates.map(dateStr => {
      const dayBookings = bookings.filter(b => b.date === dateStr && b.status !== 'cancelled');
      const ca = dayBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0);
      const d = new Date(dateStr + 'T12:00:00');
      const label = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
      return { dateStr, label, ca, count: dayBookings.length };
    });
  }, [bookings]);

  // =====================
  // 3. PAIEMENTS EN ATTENTE
  // =====================
  const unpaidBookings = useMemo(() => {
    return bookings
      .filter(b => !b.isPaid && b.totalPrice > 0 && b.status !== 'cancelled')
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [bookings]);

  // ============================================
  // ENCAISSEMENT DU SOLDE
  // ============================================
  // bookingToEncash : la résa pour laquelle on ouvre la modal d'encaissement
  const [bookingToEncash, setBookingToEncash] = useState(null);

  // Ouvrir la modal pour encaisser/marquer payé
  const handleOpenEncashModal = (booking) => {
    setBookingToEncash(booking);
  };

  // Confirmer l'encaissement (appelé depuis la modal avec la méthode choisie)
  const handleConfirmEncash = async (paymentMethod) => {
    if (!bookingToEncash) return;
    try {
      const hasDeposit = bookingToEncash.depositPaid === true;
      await onUpdateBookingPayment(bookingToEncash.id, true, {
        method: paymentMethod,
        balanceOnly: hasDeposit
      });
      setBookingToEncash(null);
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Conservée pour compatibilité avec les anciens appels qui ne passent pas par la modal
  const handleMarkPaid = async (bookingId) => {
    try {
      await onUpdateBookingPayment(bookingId, true);
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // =====================
  // 4. TAUX DE REMPLISSAGE
  // =====================
  const fillRate = useMemo(() => {
    const todaySessions = sessions.filter(s => s.date === todayStr && s.isOpen);
    const totalCapacity = todaySessions.length * MAX_PLAYERS_PER_SLOT;
    const totalPlayers = todaySessions.reduce((sum, s) => sum + (s.participantCount || 0), 0);
    const rate = totalCapacity > 0 ? Math.round((totalPlayers / totalCapacity) * 100) : 0;
    const openSlots = todaySessions.length;
    const busySlots = todaySessions.filter(s => (s.participantCount || 0) > 0).length;
    return { rate, totalPlayers, totalCapacity, openSlots, busySlots };
  }, [sessions, todayStr]);

  // Formater une date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // =====================
  // 4b. STATS AVANCÉES
  // =====================
  
  // CA par formule (toutes dates)
  const statsByFormula = useMemo(() => {
    const stats = {};
    bookings.filter(b => b.status !== 'cancelled').forEach(b => {
      const key = b.formulaName || 'Autre';
      if (!stats[key]) stats[key] = { name: key, count: 0, revenue: 0, players: 0 };
      stats[key].count++;
      stats[key].revenue += b.totalPrice || 0;
      stats[key].players += b.nbParticipants || 0;
    });
    return Object.values(stats).sort((a, b) => b.revenue - a.revenue);
  }, [bookings]);

  // Meilleur jour de la semaine
  const bestDay = useMemo(() => {
    const days = { 0: 'Dimanche', 1: 'Lundi', 2: 'Mardi', 3: 'Mercredi', 4: 'Jeudi', 5: 'Vendredi', 6: 'Samedi' };
    const dayStats = {};
    bookings.filter(b => b.status !== 'cancelled').forEach(b => {
      if (!b.date) return;
      const d = new Date(b.date + 'T12:00:00');
      const dayNum = d.getDay();
      if (!dayStats[dayNum]) dayStats[dayNum] = { name: days[dayNum], count: 0, revenue: 0 };
      dayStats[dayNum].count++;
      dayStats[dayNum].revenue += b.totalPrice || 0;
    });
    const sorted = Object.values(dayStats).sort((a, b) => b.revenue - a.revenue);
    return sorted[0] || null;
  }, [bookings]);

  // Comparaison semaine actuelle vs semaine précédente
  const weekComparison = useMemo(() => {
    const thisWeek = getWeekDates();
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekDates = [];
    const d = new Date(lastWeekStart);
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    for (let i = 0; i < 7; i++) {
      const dd = new Date(d);
      dd.setDate(d.getDate() + i);
      lastWeekDates.push(toDateStr(dd));
    }
    
    const thisWeekCA = bookings.filter(b => thisWeek.includes(b.date) && b.status !== 'cancelled').reduce((s, b) => s + (b.totalPrice || 0), 0);
    const lastWeekCA = bookings.filter(b => lastWeekDates.includes(b.date) && b.status !== 'cancelled').reduce((s, b) => s + (b.totalPrice || 0), 0);
    const thisWeekCount = bookings.filter(b => thisWeek.includes(b.date) && b.status !== 'cancelled').length;
    const lastWeekCount = bookings.filter(b => lastWeekDates.includes(b.date) && b.status !== 'cancelled').length;
    
    const caChange = lastWeekCA > 0 ? Math.round(((thisWeekCA - lastWeekCA) / lastWeekCA) * 100) : 0;
    
    return { thisWeekCA, lastWeekCA, thisWeekCount, lastWeekCount, caChange };
  }, [bookings, today]);

  // Stats globales saison
  const seasonStats = useMemo(() => {
    const active = bookings.filter(b => b.status !== 'cancelled');
    const totalCA = active.reduce((s, b) => s + (b.totalPrice || 0), 0);
    const totalPlayers = active.reduce((s, b) => s + (b.nbParticipants || 0), 0);
    const avgPerBooking = active.length > 0 ? Math.round(totalCA / active.length) : 0;
    return { totalCA, totalBookings: active.length, totalPlayers, avgPerBooking };
  }, [bookings]);

  // =====================
  // 5. BASE CLIENTS
  // =====================
  const filteredContacts = useMemo(() => {
    if (!contacts) return [];
    if (!contactSearch) return contacts;
    const search = contactSearch.toLowerCase();
    return contacts.filter(c => 
      (c.name || '').toLowerCase().includes(search) ||
      (c.email || '').toLowerCase().includes(search) ||
      (c.phone || '').includes(search)
    );
  }, [contacts, contactSearch]);

  // Export CSV
  const exportContactsCSV = () => {
    if (!contacts || contacts.length === 0) {
      alert("Aucun contact à exporter");
      return;
    }
    
    const headers = ['Nom', 'Email', 'Téléphone', 'Nb réservations', 'Total dépensé (€)', 'Première réservation', 'Dernière réservation', 'Dernière formule', 'Newsletter'];
    const rows = contacts.map(c => [
      c.name || '',
      c.email || '',
      c.phone || '',
      c.totalBookings || 0,
      c.totalSpent || 0,
      c.firstBookingDate || '',
      c.lastBookingDate || '',
      c.lastFormula || '',
      c.newsletter ? 'Oui' : 'Non'
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    
    // BOM pour que Excel reconnaisse l'UTF-8
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contacts-laserbay-${todayStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Export emails uniquement (pour mailing)
  const exportEmailsOnly = () => {
    if (!contacts) return;
    const emails = contacts
      .filter(c => c.email && c.newsletter !== false)
      .map(c => c.email);
    
    if (emails.length === 0) {
      alert("Aucun email à exporter");
      return;
    }
    
    const blob = new Blob([emails.join('\n')], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `emails-laserbay-${todayStr}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Rediriger si pas admin (après tous les hooks)
  if (!isAdmin) {
    navigate('/admin');
    return null;
  }

  return (
    <div className="dashboard-container">
      {/* En-tête */}
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h1>📊 Dashboard Admin</h1>
          <span className="dashboard-date">
            {today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div className="dashboard-actions">
          <button onClick={() => navigate(`/daily/${todayStr}`)} className="goto-today-btn">
            📅 Voir aujourd'hui
          </button>
          <button onClick={() => navigate('/')} className="goto-calendar-btn">
            🗓️ Calendrier
          </button>
          <button onClick={logout} className="logout-btn">
            🚪 Déconnexion
          </button>
        </div>
      </div>

      {/* ====== SECTION 1 : Réservations du jour ====== */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>🎯 Réservations d'aujourd'hui</h2>
          <span className="section-badge">{todayBookings.length}</span>
        </div>

        {nextBooking && (
          <div className="next-booking-alert">
            <span className="next-label">Prochaine :</span>
            <span className="next-time">{nextBooking.startTime}</span>
            <span className="next-name">{nextBooking.contactName}</span>
            <span className="next-formula">{nextBooking.formulaName}</span>
            <span className="next-players">{nextBooking.nbParticipants}p</span>
          </div>
        )}

        {todayBookings.length > 0 ? (
          <div className="today-bookings-list">
            {todayBookings.map(booking => {
              const formula = getFormulaById(booking.formulaId);
              const isPast = (booking.startTime || '') < now;
              // Statut de paiement (3 cas : intégralement payé / acompte versé / non payé)
              const fullyPaid = booking.isPaid;
              const depositOnly = !booking.isPaid && booking.depositPaid === true;
              return (
                <div 
                  key={booking.id} 
                  className={`today-booking-row ${isPast ? 'past' : ''}`}
                  onClick={() => navigate(`/daily/${todayStr}`)}
                >
                  <span className="tb-time">{booking.startTime}</span>
                  <span className="tb-icon">{formula?.icon || '📋'}</span>
                  <span className="tb-name">{booking.contactName}</span>
                  <span className="tb-formula">{booking.formulaName}</span>
                  <span className="tb-players">{booking.nbParticipants}p</span>
                  <span className="tb-price">{booking.totalPrice}€</span>
                  <span 
                    className={`tb-paid ${fullyPaid ? 'paid' : depositOnly ? 'deposit' : 'unpaid'}`}
                    title={
                      fullyPaid ? 'Intégralement payé' :
                      depositOnly ? `Acompte de ${booking.depositAmount}€ versé — solde ${booking.remainingAmount}€ dû sur place` :
                      'Non payé'
                    }
                  >
                    {fullyPaid ? '✓' : depositOnly ? '🔒' : '€'}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-section">Aucune réservation aujourd'hui</div>
        )}
      </div>

      {/* ====== SECTION 2 : Chiffre d'affaires ====== */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>💰 Chiffre d'affaires</h2>
          <div className="period-tabs">
            <button 
              className={`period-tab ${periodFilter === 'today' ? 'active' : ''}`}
              onClick={() => setPeriodFilter('today')}
            >Jour</button>
            <button 
              className={`period-tab ${periodFilter === 'week' ? 'active' : ''}`}
              onClick={() => setPeriodFilter('week')}
            >Semaine</button>
            <button 
              className={`period-tab ${periodFilter === 'month' ? 'active' : ''}`}
              onClick={() => setPeriodFilter('month')}
            >Mois</button>
          </div>
        </div>

        <div className="revenue-cards">
          <div className="revenue-card total">
            <div className="rc-label">CA Total</div>
            <div className="rc-value">{revenue.totalCA}€</div>
            <div className="rc-detail">{revenue.nbBookings} réservation(s) · {revenue.nbPlayers} joueur(s)</div>
          </div>
          <div className="revenue-card paid">
            <div className="rc-label">Encaissé</div>
            <div className="rc-value">{revenue.paidCA}€</div>
          </div>
          <div className="revenue-card unpaid">
            <div className="rc-label">En attente</div>
            <div className="rc-value">{revenue.unpaidCA}€</div>
          </div>
        </div>

        {/* Mini graphique semaine */}
        {periodFilter === 'week' && (
          <div className="weekly-chart">
            {weeklyBreakdown.map(day => {
              const maxCA = Math.max(...weeklyBreakdown.map(d => d.ca), 1);
              const height = Math.max((day.ca / maxCA) * 100, 4);
              const isToday = day.dateStr === todayStr;
              return (
                <div key={day.dateStr} className={`chart-bar-container ${isToday ? 'today' : ''}`}>
                  <div className="chart-value">{day.ca > 0 ? `${day.ca}€` : ''}</div>
                  <div className="chart-bar" style={{ height: `${height}px` }}></div>
                  <div className="chart-label">{day.label}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ====== SECTION 3 : Paiements en attente ====== */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>⏳ Paiements en attente</h2>
          <span className="section-badge warning">{unpaidBookings.length}</span>
        </div>

        {unpaidBookings.length > 0 ? (
          <div className="unpaid-list">
            {unpaidBookings.map(booking => {
              const formula = getFormulaById(booking.formulaId);
              const hasDeposit = booking.depositPaid === true;
              // Si acompte versé, on affiche le solde restant. Sinon le total.
              const amountDue = hasDeposit 
                ? (booking.remainingAmount || 0) 
                : (booking.totalPrice || 0);
              return (
                <div key={booking.id} className={`unpaid-row ${hasDeposit ? 'has-deposit' : ''}`}>
                  <span className="up-date">{formatDate(booking.date)}</span>
                  <span className="up-time">{booking.startTime}</span>
                  <span className="up-icon">{formula?.icon || '📋'}</span>
                  <span className="up-name">{booking.contactName}</span>
                  <span className="up-price">
                    {hasDeposit ? (
                      <>
                        <span className="up-deposit-tag" title={`Acompte de ${booking.depositAmount}€ déjà versé`}>
                          🔒 acompte OK
                        </span>
                        <span className="up-remaining"> · solde {amountDue}€</span>
                      </>
                    ) : (
                      <span>{booking.totalPrice}€</span>
                    )}
                  </span>
                  {booking.contactPhone && (
                    <a href={`tel:${booking.contactPhone}`} className="up-phone">📞</a>
                  )}
                  <button 
                    className="mark-paid-btn"
                    onClick={() => handleOpenEncashModal(booking)}
                    title={hasDeposit ? `Encaisser le solde de ${amountDue}€` : `Marquer comme payé (${booking.totalPrice}€)`}
                  >
                    {hasDeposit ? `💶 Encaisser ${amountDue}€` : '✓ Soldé'}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-section success">Tous les paiements sont à jour !</div>
        )}

        {unpaidBookings.length > 0 && (
          <div className="unpaid-total">
            Total en attente : <strong>
              {unpaidBookings.reduce((sum, b) => {
                const hasDeposit = b.depositPaid === true;
                return sum + (hasDeposit ? (b.remainingAmount || 0) : (b.totalPrice || 0));
              }, 0)}€
            </strong>
          </div>
        )}
      </div>

      {/* ====== SECTION 4 : Taux de remplissage ====== */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>📈 Remplissage du jour</h2>
        </div>

        <div className="fill-rate-display">
          <div className="fill-gauge">
            <svg viewBox="0 0 120 120" className="gauge-svg">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#e2e8f0" strokeWidth="10" />
              <circle 
                cx="60" cy="60" r="50" fill="none" 
                stroke={fillRate.rate > 70 ? '#10b981' : fillRate.rate > 30 ? '#f59e0b' : '#94a3b8'} 
                strokeWidth="10"
                strokeDasharray={`${fillRate.rate * 3.14} 314`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
              <text x="60" y="55" textAnchor="middle" className="gauge-text">{fillRate.rate}%</text>
              <text x="60" y="72" textAnchor="middle" className="gauge-subtext">rempli</text>
            </svg>
          </div>

          <div className="fill-stats">
            <div className="fill-stat">
              <span className="fs-value">{fillRate.openSlots}</span>
              <span className="fs-label">créneaux ouverts</span>
            </div>
            <div className="fill-stat">
              <span className="fs-value">{fillRate.busySlots}</span>
              <span className="fs-label">avec réservations</span>
            </div>
            <div className="fill-stat">
              <span className="fs-value">{fillRate.totalPlayers}</span>
              <span className="fs-label">joueurs inscrits</span>
            </div>
            <div className="fill-stat">
              <span className="fs-value">{fillRate.totalCapacity}</span>
              <span className="fs-label">capacité totale</span>
            </div>
          </div>
        </div>
      </div>

      {/* ====== SECTION 4b : Stats avancées ====== */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>📊 Statistiques</h2>
        </div>

        {/* Stats globales saison */}
        <div className="stats-global">
          <div className="stat-card">
            <div className="stat-value">{seasonStats.totalCA}€</div>
            <div className="stat-label">CA saison</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{seasonStats.totalBookings}</div>
            <div className="stat-label">réservations</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{seasonStats.totalPlayers}</div>
            <div className="stat-label">joueurs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{seasonStats.avgPerBooking}€</div>
            <div className="stat-label">panier moyen</div>
          </div>
        </div>

        {/* Comparaison semaine */}
        <div className="week-comparison">
          <h3>Semaine en cours vs précédente</h3>
          <div className="comparison-row">
            <div className="comparison-item">
              <span className="comp-label">Cette semaine</span>
              <span className="comp-value">{weekComparison.thisWeekCA}€</span>
              <span className="comp-detail">{weekComparison.thisWeekCount} rés.</span>
            </div>
            <div className="comparison-arrow">
              {weekComparison.caChange > 0 ? '📈' : weekComparison.caChange < 0 ? '📉' : '➡️'}
            </div>
            <div className="comparison-item">
              <span className="comp-label">Semaine dernière</span>
              <span className="comp-value">{weekComparison.lastWeekCA}€</span>
              <span className="comp-detail">{weekComparison.lastWeekCount} rés.</span>
            </div>
            {weekComparison.caChange !== 0 && (
              <div className={`comparison-badge ${weekComparison.caChange > 0 ? 'positive' : 'negative'}`}>
                {weekComparison.caChange > 0 ? '+' : ''}{weekComparison.caChange}%
              </div>
            )}
          </div>
        </div>

        {/* CA par formule */}
        {statsByFormula.length > 0 && (
          <div className="stats-by-formula">
            <h3>CA par formule</h3>
            <div className="formula-stats-list">
              {statsByFormula.map((f, i) => {
                const maxRev = statsByFormula[0]?.revenue || 1;
                const barWidth = Math.max((f.revenue / maxRev) * 100, 4);
                return (
                  <div key={f.name} className="formula-stat-row">
                    <span className="fsr-rank">#{i + 1}</span>
                    <span className="fsr-name">{f.name}</span>
                    <div className="fsr-bar-container">
                      <div className="fsr-bar" style={{ width: `${barWidth}%` }}></div>
                    </div>
                    <span className="fsr-revenue">{f.revenue}€</span>
                    <span className="fsr-count">{f.count} rés.</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Meilleur jour */}
        {bestDay && (
          <div className="best-day">
            🏆 Meilleur jour : <strong>{bestDay.name}</strong> — {bestDay.revenue}€ de CA ({bestDay.count} réservation{bestDay.count > 1 ? 's' : ''})
          </div>
        )}
      </div>

      {/* ====== SECTION 5b : Cartes cadeaux ====== */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>🎁 Cartes cadeaux</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="section-badge">{giftCards ? giftCards.length : 0}</span>
            <button 
              className="toggle-section-btn"
              onClick={() => setShowGiftCards(!showGiftCards)}
            >
              {showGiftCards ? '🔽 Masquer' : '▶️ Afficher'}
            </button>
          </div>
        </div>

        {showGiftCards && (
          <>
            {/* Recherche par code */}
            <div className="gc-admin-search">
              <input
                type="text"
                placeholder="Rechercher par code (LB-...) ou nom..."
                value={giftCardSearch}
                onChange={(e) => setGiftCardSearch(e.target.value)}
                className="contacts-search"
              />
            </div>

            {giftCards && giftCards.length > 0 ? (
              <div className="gc-admin-list">
                {giftCards
                  .filter(gc => {
                    if (!giftCardSearch) return true;
                    const search = giftCardSearch.toLowerCase();
                    return (gc.code || '').toLowerCase().includes(search) 
                      || (gc.senderName || '').toLowerCase().includes(search)
                      || (gc.recipientName || '').toLowerCase().includes(search);
                  })
                  .sort((a, b) => (b.createdAt || '').toString().localeCompare((a.createdAt || '').toString()))
                  .map(gc => (
                    <div key={gc.id} className={`gc-admin-row ${gc.status}`}>
                      <div className="gc-admin-main">
                        <span className="gc-admin-code">{gc.code}</span>
                        <span className={`gc-admin-status ${gc.status}`}>
                          {gc.status === 'active' ? '✅ Active' : 
                           gc.status === 'used' ? '☑️ Utilisée' : 
                           gc.status === 'pending' ? '⏳ En attente' : 
                           gc.status === 'expired' ? '❌ Expirée' : gc.status}
                        </span>
                        <span className="gc-admin-amount">{gc.amount}€</span>
                      </div>
                      <div className="gc-admin-details">
                        <span>De : <strong>{gc.senderName}</strong></span>
                        <span>Pour : <strong>{gc.recipientName}</strong></span>
                        {gc.formulaName && <span>Formule : {gc.formulaName}</span>}
                        <span>Valide jusqu'au : {gc.validUntil}</span>
                      </div>
                      <div className="gc-admin-actions">
                        {gc.status === 'active' && onMarkGiftCardUsed && (
                          <button 
                            className="gc-admin-use-btn"
                            onClick={() => {
                              if (window.confirm(`Marquer la carte ${gc.code} comme utilisée ?`)) {
                                onMarkGiftCardUsed(gc.code, gc.id);
                              }
                            }}
                          >
                            ☑️ Utilisée
                          </button>
                        )}
                        {onDeleteGiftCard && (
                          <button 
                            className="gc-admin-delete-btn"
                            onClick={() => {
                              if (window.confirm(`Supprimer définitivement la carte ${gc.code} ?`)) {
                                onDeleteGiftCard(gc.id);
                              }
                            }}
                          >
                            🗑️ Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className="empty-section">Aucune carte cadeau pour le moment</div>
            )}
          </>
        )}
      </div>

      {/* ====== SECTION 6 : Base clients ====== */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>👥 Base clients</h2>
          <span className="section-badge">{contacts ? contacts.length : 0}</span>
        </div>

        <div className="contacts-toolbar">
          <input
            type="text"
            placeholder="Rechercher un client (nom, email, tél)..."
            value={contactSearch}
            onChange={(e) => setContactSearch(e.target.value)}
            className="contacts-search"
          />
          <div className="contacts-export-buttons">
            <button onClick={exportContactsCSV} className="export-btn csv">
              📊 Export CSV
            </button>
            <button onClick={exportEmailsOnly} className="export-btn emails">
              📧 Export emails
            </button>
          </div>
        </div>

        {filteredContacts.length > 0 ? (
          <div className="contacts-list">
            <div className="contacts-header-row">
              <span className="ch-name">Nom</span>
              <span className="ch-email">Email</span>
              <span className="ch-phone">Tél</span>
              <span className="ch-bookings">Rés.</span>
              <span className="ch-spent">Dépensé</span>
              <span className="ch-actions"></span>
            </div>
            {filteredContacts.slice(0, 50).map(contact => (
              <div key={contact.id} className="contact-row">
                <span className="cr-name">
                  {getLoyaltyLevel(contact.totalBookings || 0).icon} {contact.name}
                </span>
                <span className="cr-email">{contact.email || '—'}</span>
                <span className="cr-phone">{contact.phone || '—'}</span>
                <span className="cr-bookings">{contact.totalBookings || 0}</span>
                <span className="cr-spent">{contact.totalSpent || 0}€</span>
                <span className="cr-actions">
                  {onDeleteContact && (
                    <button 
                      className="contact-delete-btn"
                      onClick={() => {
                        if (window.confirm(`Supprimer ${contact.name} de la base clients ? (RGPD)`)) {
                          onDeleteContact(contact.id);
                        }
                      }}
                      title="Supprimer (RGPD)"
                    >
                      🗑️
                    </button>
                  )}
                </span>
              </div>
            ))}
            {filteredContacts.length > 50 && (
              <div className="contacts-more">
                ... et {filteredContacts.length - 50} autres contacts. Exportez le CSV pour la liste complète.
              </div>
            )}
          </div>
        ) : (
          <div className="empty-section">
            {contactSearch ? 'Aucun résultat' : 'Aucun client enregistré pour le moment'}
          </div>
        )}
      </div>

      {/* Modal d'encaissement du solde */}
      {bookingToEncash && (
        <PaymentBalanceModal
          booking={bookingToEncash}
          onConfirm={handleConfirmEncash}
          onCancel={() => setBookingToEncash(null)}
        />
      )}
    </div>
  );
}

export default Dashboard;
